import React, { useState } from "react";
import {
  Button,
  Input,
  Select,
  Modal,
  Table,
  Card,
  notification,
  Spin,
  Dropdown,
} from "antd";
import {
  SearchOutlined,
  CloseOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { LayoutGrid, List, Plus } from "lucide-react";
import { LuFilter } from "react-icons/lu";
import dummyvideo from "../../../assets/videos/Placeholder Video 1.mp4";
import { useNavigate } from "react-router-dom";
import CreateFormModal from "../../../components/Modals/CreateFormModal";
import { ACTIONS_DROPDOWN_PROPS } from "../../../constants/actionsDropdown";
import {
  useDeleteFormMutation,
  useDeleteQuestionMutation,
  useGetFormTemplatesQuery,
  useGetQuestionTemplatesQuery,
} from "../../../store/api/apiSlice";
import { basePath } from "../../../utils";

const Templates = () => {
  const [activeTab, setActiveTab] = useState("forms");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [viewType, setViewType] = useState(isMobile ? "grid" : "list");
  const [searchText, setSearchText] = useState("");
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [formType, setFormType] = useState(undefined);

  const navigate = useNavigate();

  const {
    data: formTemplates = [],
    isLoading: isLoadingForms,
    refetch: refetchForms,
  } = useGetFormTemplatesQuery();

  const {
    data: questionTemplates = [],
    isLoading: isLoadingQuestions,
    refetch: refetchQuestions,
  } = useGetQuestionTemplatesQuery();
  const [deleteForm] = useDeleteFormMutation();
  const [deleteQuestion] = useDeleteQuestionMutation();

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const clearSearch = () => {
    setSearchText("");
  };

  // Handle form type filter change
  const handleFormTypeChange = (value) => {
    setFormType(value);
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchText("");
    setFormType(undefined);
  };

  const handleDelete = (record, isQuestion = false) => {
    const deleteMutation = isQuestion ? deleteQuestion : deleteForm;

    Modal.confirm({
      title: `Delete ${isQuestion ? "Question" : "Form"} Template`,
      content: "Are you sure you want to delete this template?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteMutation(record._id);
          // notification.success({
          //   message: 'Success',
          //   description: 'Template deleted successfully'
          // });
          if (isQuestion) {
            refetchQuestions();
          } else {
            refetchForms();
          }
        } catch (error) {
          notification.error({
            message: "Error",
            description: "Failed to delete template",
          });
        }
      },
    });
  };

  const handleSuccess = () => {
    setEditModalVisible(false);
    setIsModalVisible(false);
    setEditingForm(null);

    // Force refetch the data
    if (activeTab === "forms") {
      refetchForms();
    } else {
      refetchQuestions();
    }
  };

  const handleEdit = (record, isQuestion = false) => {
    setEditingForm(record);
    setEditModalVisible(true);
  };

  const getActionsMenuItems = () => [
    { key: "edit", label: "Edit", icon: <EditOutlined /> },
    {
      key: "delete",
      label: "Delete",
      danger: true,
      icon: <DeleteOutlined />,
    },
  ];

  const handleActionMenuClick = (record, actionKey, event, isQuestion = false) => {
    event?.domEvent?.stopPropagation?.();

    switch (actionKey) {
      case "edit":
        if (isQuestion) {
          navigate(
            `/${basePath}/template-form/${"67af32593cb9b82ab45929c0"}/questions/edit/${record._id}`,
          );
        } else {
          handleEdit(record);
        }
        break;
      case "delete":
        handleDelete(record, isQuestion);
        break;
      default:
        break;
    }
  };

  const renderActionsDropdown = (record, isQuestion = false) => (
    <div
      className="flex items-center justify-center"
      onClick={(e) => e.stopPropagation()}
    >
      <Dropdown
        {...ACTIONS_DROPDOWN_PROPS}
        menu={{
          items: getActionsMenuItems(),
          onClick: (event) =>
            handleActionMenuClick(record, event.key, event, isQuestion),
        }}
      >
        <button
          type="button"
          className="actions-dots-btn"
          onClick={(e) => e.stopPropagation()}
          aria-label={`Open actions for ${record?.title || "template"}`}
        >
          <MoreOutlined />
        </button>
      </Dropdown>
    </div>
  );

  // Filter form templates based on search text and form type
  const filteredFormTemplates = formTemplates.filter((template) => {
    const matchesSearch =
      template.title?.toLowerCase().includes(searchText.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchText.toLowerCase());
    const matchesType = !formType || template.type === formType;
    return matchesSearch && matchesType;
  });

  const filteredQuestionTemplates = questionTemplates.filter((template) => {
    return (
      template.title?.toLowerCase().includes(searchText.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchText.toLowerCase())
    );
  });

  if (isLoadingForms || isLoadingQuestions) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  const columns = [
    {
      title: "Sr#",
      dataIndex: "id",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Form",
      key: "form",
      render: (record) => (
        <div className="flex flex-col">
          <div className="font-medium text-gray-900">{record.title}</div>
          <div className="text-sm text-gray-500 truncate max-w-md">
            {record.description}
          </div>
          <span className="inline-flex px-3 py-1 rounded-full text-xs border border-black text-gray-800 w-fit mt-1">
            {record.type}
          </span>
        </div>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => renderActionsDropdown(record),
    },
  ];

  const FormCard = ({ data }) => (
    <Card
      className="hover:shadow-md transition-shadow cursor-pointer"
      bodyStyle={{ padding: "16px" }}
      onClick={() => navigate(`/admin/template-form/${data._id}`)}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="font-medium text-gray-900 mb-2">{data.title}</h3>
          <p className="text-sm text-gray-500 mb-2 line-clamp-2">
            {data.description}
          </p>
          <span className="inline-flex px-3 py-1 rounded-full text-xs border border-black text-black">
            {data.type}
          </span>
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          {renderActionsDropdown(data)}
        </div>
      </div>
    </Card>
  );

  const QuestionCard = ({ data }) => (
    <div className="bg-white rounded-xl p-4 shadow-sm border transition-shadow">
      <div className=" items-start gap-2">
        <div
          className="flex gap-2 justify-end"
          onClick={(e) => e.stopPropagation()}
        >
          {renderActionsDropdown(data, true)}
        </div>
        <div className="flex-1">
          {/* Video thumbnail with play button */}
          <div className="relative mb-4 rounded-xl overflow-hidden">
            <video
              src={dummyvideo}
              alt="Video thumbnail"
              className="w-full rounded-xl"
              controls
              preload="metadata"
            >
              <track kind="captions" />
            </video>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                >
                  <polygon points="5 3 19 12 5 21 5 3" fill="white" />
                </svg>
              </div>
            </div>
          </div>
          <h3 className="text-base font-medium text-gray-900 mb-2">
            {data.title}
          </h3>
          <p className="text-sm text-gray-500 line-clamp-2">
            {data.description}
          </p>
        </div>
      </div>
    </div>
  );

  const questionColumns = [
    {
      title: "Sr#",
      dataIndex: "id",
      width: 60,
      render: (_, __, index) => (
        <div className="flex items-center justify-center w-8 h-8">
          <span>{index + 1}</span>
        </div>
      ),
    },
    {
      title: "Question",
      key: "question",
      render: (record) => (
        <div className="flex items-start gap-4">
          <div className="relative w-40 h-24 rounded-xl overflow-hidden">
            <video
              src={dummyvideo}
              alt="Video thumbnail"
              className="w-full h-full object-cover"
              controls
              preload="metadata"
            >
              <track kind="captions" />
            </video>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                >
                  <polygon points="5 3 19 12 5 21 5 3" fill="white" />
                </svg>
              </div>
            </div>
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-gray-900 mb-1">{record.title}</h4>
            <p className="text-sm text-gray-500 line-clamp-2">
              {record.description}
            </p>
          </div>
        </div>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => renderActionsDropdown(record, true),
    },
  ];

  return (
    <div className="">
      <div className="flex flex-col md:flex-row justify-between gap-4 m-0 mt-0 mb-4 pb-2 items-end border-b border-gray-200">
        <div className="flex-1">
          <h1 className={`md:text-2l text-xl font-bold text-gray-900 mb-0 mt-0`}>
            Templates
          </h1>
          <p className="text-lightBlack font-light text-sm w-[80%] mt-1 mb-0">
            Create and manage templates for your forms and questions
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="text"
            className={`border-0 h-8 py-0 ${
              activeTab === "forms"
                ? "bg-primary text-white"
                : "text-gray-600 bg-transparent"
            } px-4 py-2 rounded-2xl`}
            onClick={() => setActiveTab("forms")}
          >
            Forms
          </button>
          <button
            type="text"
            className={`border-0 h-8 py-0 ${
              activeTab === "questions"
                ? "bg-primary text-white"
                : "text-gray-600 bg-transparent"
            } px-4 py-2 rounded-2xl`}
            onClick={() => setActiveTab("questions")}
          >
            Questions
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-6">
        <div className="flex bg-secondary rounded-xl p-1.5">
          <button
            type="button"
            className={`view-toggle-btn ${
              viewType === "list" ? "bg-primary text-white" : ""
            }`}
            onClick={() => setViewType("list")}
          >
            <List size={20} />
          </button>
          <button
            type="button"
            className={`view-toggle-btn ${
              viewType === "grid" ? "bg-primary text-white" : ""
            }`}
            onClick={() => setViewType("grid")}
          >
            <LayoutGrid size={20} />
          </button>
        </div>
        <Input
          placeholder="Search..."
          prefix={<SearchOutlined />}
          suffix={searchText && <CloseOutlined onClick={clearSearch} />}
          onChange={handleSearch}
          value={searchText}
          className="flex-1 h-12 rounded-xl"
        />
        {activeTab === "forms" && (
          <Select
            placeholder="Form Type"
            className="w-40 rounded-xl h-12"
            onChange={handleFormTypeChange}
            value={formType}
            allowClear
          >
            <Select.Option value="survey">Survey</Select.Option>
            <Select.Option value="form">Form</Select.Option>
            <Select.Option value="complaint">Complaint</Select.Option>
          </Select>
        )}
        <button
          className={`h-[48px] w-11 flex items-center justify-center rounded-xl border ${
            searchText || formType ? "text-red-500" : "text-gray-500"
          }`}
          onClick={resetFilters}
          title="Reset Filters"
        >
          <LuFilter size={20} />
        </button>
      </div>
      {activeTab === "questions" ? (
        viewType === "list" ? (
          <Table
            columns={questionColumns}
            dataSource={filteredQuestionTemplates}
            rowKey="_id"
            scroll={{ x: true }}
            pagination={false}
            className="rounded-xl"
            locale={{ emptyText: "No question templates found" }}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredQuestionTemplates.length > 0 ? (
              filteredQuestionTemplates.map((question) => (
                <QuestionCard key={question._id} data={question} />
              ))
            ) : (
              <div className="col-span-3 flex justify-center items-center py-10 text-gray-500">
                No question templates found
              </div>
            )}
          </div>
        )
      ) : viewType === "list" ? (
        <Table
          scroll={{ x: true }}
          columns={columns}
          dataSource={filteredFormTemplates}
          rowKey="_id"
          pagination={false}
          className="rounded-xl"
          onRow={(record) => ({
            onClick: (e) => {
              navigate(`/admin/template-form/${record._id}`);
            },
          })}
          locale={{ emptyText: "No form templates found" }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFormTemplates.length > 0 ? (
            filteredFormTemplates.map((template) => (
              <FormCard key={template._id} data={template} />
            ))
          ) : (
            <div className="col-span-3 flex justify-center items-center py-10 text-gray-500">
              No form templates found
            </div>
          )}
        </div>
      )}
      <CreateFormModal
        visible={editModalVisible || isModalVisible}
        onClose={() => {
          setEditModalVisible(false);
          setIsModalVisible(false);
          setEditingForm(null);
        }}
        editData={editingForm}
        onSuccess={handleSuccess}
        istemplate={true}
      />
    </div>
  );
};

export default Templates;
